// components/planner/TaskTableLauncher.jsx
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import ServiceTaskPlanner from './ServiceTaskPlanner';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../UtilContext';
import { LocaleContext } from '../LocaleContext';
import { confirmDialog } from 'primereact/confirmdialog';
import { deleteRequest } from '../../api/ApiController';

export default function TaskTableLauncher({
  serviceTaskId,
  getRequest,
  postRequest,
  employees,
  canManage,
  onSummaryChange,   // ⬅️ nový prop
  taskTheme,
  taskDescription,
  taskSenderId,
}) {
  const [state,dispatch] = useContext(DatabaseContext);
  const { setDialogText, setVisible, toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const [loading, setLoading] = useState(true);
  const [table, setTable] = useState(null);
  const [showPlanner, setShowPlanner] = useState(false);

  const [plannerKey, setPlannerKey] = useState(0);

  const [pickOpen, setPickOpen] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null); // ← náhľad vybranej šablóny
  const [replaceExisting, setReplaceExisting] = useState(false); // ak chceš vedieť nahradiť vs pridať

  const [summary, setSummary] = useState(null);
  const serviceTemplates = (state?.task_templates || []).filter((t) => (t.template_context || 'service') === 'service');

  const notify = (severity, summary, detail) =>
    toast?.current?.show({ severity, summary, detail, life: 2400 });

  // načítaj existujúcu tabuľku pri otvorení detailu
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const tableResponse = await getRequest(`/api/service_tasks/${serviceTaskId}/task_table`);
        // t je buď null/undefined, alebo priamo dáta, alebo prípadne {data: ...}
        const data = (tableResponse && typeof tableResponse === 'object' && 'data' in tableResponse) ? (tableResponse).data : tableResponse;
        console.log(data);
        if (data == null) {
          setTable(null);
          setShowPlanner(false);
        } else {
          setTable(data);
          setShowPlanner(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [serviceTaskId]);

  
  const handleTableChange = (next) => {
    if (!next) return;
    // next môže byť priamo tabuľka alebo {data: ...}
    const tableData = next?.data ?? next;
    setTable(prev => ({ ...(prev || {}), ...(tableData || {}) }));
  };

  const handleSummaryChange = (s) => {
    setSummary(s);
    // pošli súhrn aj hore do ServiceTaskDetail (ak je callback zadaný)
    if (typeof onSummaryChange === 'function') {
      onSummaryChange(s);
    }
  };

  const openTemplatePicker = async () => {
    setTemplateId(null);
    setTemplatePreview(null);
    setReplaceExisting(false);
    setPickOpen(true);
  };

  // po výbere šablóny načítaj jej obsah kvôli náhľadu
  const onPickTemplate = (tplId) => {
    setTemplateId(tplId);
    if (!tplId) { setTemplatePreview(null); return; }
    const tpl = serviceTemplates.find(t => String(t.id) === String(tplId));
    setTemplatePreview(tpl ?? null);
  };

  const createEmptyTable = async () => {
    try {
      setDialogText(t('Common.Saving'));
      setVisible(true);
      setLoading(true);
      const fd = new FormData();
      const res = await postRequest(`/api/service_tasks/${serviceTaskId}/task_tables`, fd, true);
      const payload = res?.data ?? res;
      setTable(payload?.data ?? payload);
      setShowPlanner(true);
      notify('success', t('TaskTableLauncher.ToastCreated'), t('TaskTableLauncher.EmptyTableCreated'));
    } catch {
      notify('error', t('Common.Error'), t('TaskTableLauncher.EmptyTableCreateFailed'));
    } finally {
      setVisible(false);
      setLoading(false);
    }
  };

  const deleteEmptyTable = async () => {
    if (!table || (table.rows && table.rows.length > 0)) {
      notify('warn', t('TaskTableLauncher.ToastDeleteRejectedHeader'), t('TaskTableLauncher.DeleteTableOnlyWhenEmpty'));
      return;
    }

    confirmDialog({
      message: t('TaskTableLauncher.ConfirmDeleteEmptyTable'),
      header: t('ServiceTaskPlanner.ConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: async () => {
        try {
          setDialogText(t('TaskTableLauncher.DeletingTable'));
          setVisible(true);
          setLoading(true);
          await deleteRequest(`/api/task_tables/${table.id}`, true);
          setTable(null);
          setShowPlanner(false);
          notify('success', t('TaskTableLauncher.ToastDeleted'), t('TaskTableLauncher.TableDeleted'));
        } catch (e) {
          notify('error', t('Common.Error'), t('TaskTableLauncher.TableDeleteFailed'));
        } finally {
          setVisible(false);
          setLoading(false);
        }
      },
    });
  };

    // použitie šablóny – len natiahnuť riadky (bez prepisov zodpovedných)
    const applyTemplate = async () => {
      if (!templateId) return;
      try {
        setDialogText(t('TaskTableLauncher.ApplyingTemplate'));
        setVisible(true);
        setLoading(true);
        const fd = new FormData();
        fd.append('template_id', templateId);
        fd.append('reset_rows', replaceExisting ? '1' : '0');

        const res = await postRequest(
          `/api/service_tasks/${serviceTaskId}/task_tables/from_template`,
          fd,
          true
        );
        const payload = res?.data ?? res;
        const tableData = payload?.data ?? payload;   // mal by to byť objekt tabuľky s id

        // ⚠️ hneď refetchni detail tabuľky, nech máš čerstvé rows zoradené
        const r = await getRequest(`/api/task_tables/${tableData.id}`);
        const full = r?.data ?? r;
        const fresh = full?.data ?? full;

        setTable(fresh);
        setPickOpen(false);
        setShowPlanner(true);
        // donúť planner, aby sa re-mountol (vyresetuje vnútorné stavy)
        setPlannerKey(k => k + 1);

        notify(
          'success',
          t('TaskTableLauncher.TemplateAppliedTitle'),
          replaceExisting ? t('TaskTableLauncher.TemplateAppliedReplaced') : t('TaskTableLauncher.TemplateAppliedAppended')
        );
      } catch {
        notify('error', t('Common.Error'), t('TaskTableLauncher.TemplateApplyFailed'));
      } finally {
        setVisible(false);
        setLoading(false);
      }
    };

  // pre náhľad: mapni assignee_id → meno zamestnanca
  const employeeNameById = (id) => {
    const u = (employees || []).find(x => String(x.id) === String(id));
    return u ? (u?.employee?.name ?? u?.username ?? `#${id}`) : (id ? `#${id}` : '');
    // ak do šablóny ukladáš priamo Employee meno, uprav si to sem
  };

  const HeaderBar = (
    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded">
      <div className="flex items-center gap-3">
        <i className="pi pi-table text-blue-700" />
        <div>
          <div className="text-sm text-blue-900 font-semibold">
            {t('TaskTableLauncher.TableTasksPrefix')}{table ? ` – ${table.title || t('TaskTableLauncher.NoTableName')}` : ''}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!table && (
          <div className="flex gap-2">
            <Button label={t('TaskTableLauncher.ButtonEmptyTable')} icon="pi pi-plus" onClick={createEmptyTable} disabled={loading || !canManage} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
            <Button label={t('TaskTableLauncher.ButtonFromTemplate')} icon="pi pi-table" onClick={openTemplatePicker} disabled={loading || !canManage} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }} />
          </div>
        )}
        {table && (
          <>
            <Button
              label={showPlanner ? t('TaskTableLauncher.ButtonHidePlanner') : t('TaskTableLauncher.ButtonShowPlanner')}
              icon="pi pi-pencil"
              onClick={() => setShowPlanner(v => !v)}
              disabled={!canManage}
              style={{ textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: 6 }}
            />

            <Button
              label={t('TaskTableLauncher.ButtonFromTemplate')}
              icon="pi pi-table"
              onClick={openTemplatePicker}
              disabled={!canManage}
              style={{ textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: 6 }}
            />

            {/* 👇 nové tlačidlo – len ak je tabuľka prázdna */}
            {(!table.rows || table.rows.length === 0) && (
              <Button
                label={t('TaskTableLauncher.ButtonDeleteTable')}
                icon="pi pi-trash"
                severity="danger"
                onClick={deleteEmptyTable}
                disabled={!canManage || loading}
                style={{
                  textDecoration: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {HeaderBar}

      {(table && showPlanner) && (
        <div className="mt-3 min-w-0">
          <ServiceTaskPlanner
            key={plannerKey}                 // ← re-mount pri zmene
            serviceTaskId={serviceTaskId}
            taskTableId={table.id}
            getRequest={getRequest}
            postRequest={postRequest}
            employees={employees}
            canManage={canManage}
            onTableChange={handleTableChange}      // ⬅️ nové
            onSummaryChange={handleSummaryChange}  // ⬅️ nové
            taskTheme={taskTheme}
            taskDescription={taskDescription}
            taskSenderId={taskSenderId}
          />
        </div>
      )}

      <Dialog
        header={t('TaskTableLauncher.PickTemplateHeader')}
        visible={pickOpen}
        style={{ width: '44rem' }}
        onHide={() => setPickOpen(false)}
      >
        <div className="flex flex-col gap-3">
          <Dropdown
            className="w-full"
            placeholder={t('TaskTableLauncher.PickTemplatePlaceholder')}
            options={serviceTemplates.map(t => ({ label: t.name, value: t.id }))}
            value={templateId}
            onChange={(e) => onPickTemplate(e.value)}
            filter
          />

          {/* Náhľad šablóny */}
          {templatePreview?.rows_json?.length > 0 && (
            <div className="border rounded p-2">
              <div className="text-sm font-medium mb-2">{t('TaskTableLauncher.TemplatePreviewTitle')} – {templatePreview.name}</div>
              <DataTable value={templatePreview.rows_json} size="small" tableStyle={{ minWidth: 600 }}>
                <Column header="#" body={(r, o) => o.rowIndex + 1} style={{ width: '3.5rem' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewTaskName')} field="title" style={{ width: '260px' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewAssignee')} body={(r) => <span>{employeeNameById(r.assignee_id)}</span>} style={{ width: '200px' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewDuration')} body={(r) => <span>{`${r.duration_days ?? 0} ${t('ServiceTaskPlanner.DaysShort')}`}</span>} style={{ width: '110px' }} />
                <Column header={t('TaskTableLauncher.TemplatePreviewChaining')} body={(r) => <span>{r.chain_mode === 'parallel_prev' ? '||' : '→'}</span>} style={{ width: '140px' }} />
              </DataTable>
            </div>
          )}

          {/* Voľba nahradiť/pridať – ak nechceš, vyhoď tento blok */}
          <label className="flex items-center gap-2 text-sm mt-1">
            <input type="checkbox" checked={replaceExisting} onChange={e=>setReplaceExisting(e.target.checked)} />
            {t('TaskTableLauncher.ReplaceExistingLabel')}
          </label>

          <div className="flex justify-end gap-2 mt-2">
            <Button label={t('TaskTableLauncher.ButtonCancel')} severity="secondary" onClick={() => setPickOpen(false)} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}/>
            <Button label={t('TaskTableLauncher.ButtonApplyTemplate')} icon="pi pi-check" onClick={applyTemplate} disabled={!templateId} style={{ textDecoration: 'none', padding: '0.25rem 0.5rem', borderRadius: 6 }}/>
          </div>
        </div>
      </Dialog>
    </div>
  );
}